import { useForm } from 'react-hook-form';

import { z } from 'zod';

import { showPromiseToast } from '@/lib/toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useCreateDevUserMutation } from '@/store/api/devUserApi';

import { zodResolver } from '@hookform/resolvers/zod';

const createDevUserSchema = z.object({
  dev_username: z
    .string()
    .min(1, 'Username is required')
    .regex(
      /^[a-z0-9_-]+$/,
      'Username must contain only lowercase letters, numbers, hyphens, and underscores',
    ),
  user_to_copy: z
    .string()
    .min(1, 'User to copy is required')
    .regex(
      /^[a-z0-9_-]+$/,
      'Username must contain only lowercase letters, numbers, hyphens, and underscores',
    ),
});

type CreateDevUserFormData = z.infer<typeof createDevUserSchema>;

interface CreateDevUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: string;
  environment: string;
}

export const CreateDevUserModal = ({
  open,
  onOpenChange,
  client,
  environment,
}: CreateDevUserModalProps) => {
  const [createDevUser] = useCreateDevUserMutation();

  const form = useForm<CreateDevUserFormData>({
    resolver: zodResolver(createDevUserSchema),
    defaultValues: {
      dev_username: '',
      user_to_copy: '',
    },
  });

  const onSubmit = async (data: CreateDevUserFormData) => {
    await showPromiseToast(
      createDevUser({
        client_name: client,
        environment: environment,
        dev_username: data.dev_username,
        user_to_copy: data.user_to_copy,
      }).unwrap(),
      {
        loading: `Creating dev user ${data.dev_username}...`,
        success: (response) =>
          `Dev user created successfully! ${response.message}. You will receive an email notification when complete.`,
        error: (error: any) => error?.data?.detail || error?.message || 'Failed to create dev user',
      },
    );

    // Reset form and close modal
    form.reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Create Dev User</DialogTitle>
          <DialogDescription>
            Create a new dev user for <span className='font-semibold'>{client}</span> •{' '}
            <span className='font-semibold'>{environment}</span>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4 py-4'>
            {/* Dev Username Input */}
            <FormField
              control={form.control}
              name='dev_username'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Username</FormLabel>
                  <FormControl>
                    <Input placeholder='Enter username to create (e.g., john_doe)' {...field} />
                  </FormControl>
                  <FormDescription>
                    Username must contain only lowercase letters, numbers, hyphens, and underscores
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* User to Copy Input */}
            <FormField
              control={form.control}
              name='user_to_copy'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Copy From User</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Enter existing username to copy (e.g., jane_smith)'
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The new user will inherit permissions and data from this existing user
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Info Message */}
            <div className='rounded-md bg-blue-50 p-3 border border-blue-200'>
              <p className='text-xs text-blue-700'>
                The dev user will be created on the bastion host. You will receive an email
                notification when the user creation completes.
              </p>
            </div>

            {/* Actions */}
            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancel}
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
